import React, { useId } from "react";
import { getVariantLabel, type ProductVariant } from "@/types/product";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./VariantSelector.module.css";

export interface VariantSelectorProps {
  variants: ProductVariant[];
  /** Currently selected variant, or null while the customer has not picked one. */
  selectedVariant: ProductVariant | null;
  onSelect: (variantId: string) => void;
}

/**
 * Version picker shown above the Add-to-cart button for products sold in
 * several variants (collar size, colour, ...).
 *
 * Nothing is preselected on purpose: the size/colour has to be a deliberate
 * choice, so Add-to-cart stays disabled until one is picked. Rendered as native
 * radios (visually pills) so keyboard and screen-reader behaviour comes for free.
 *
 * The heading is the `Tracker.Products.ChooseVariant` dictionary item rather
 * than the variant attribute's own name — Commerce translates attribute
 * *values* but not their key names, so the key name would show up in English on
 * a Swedish page.
 */
export const VariantSelector: React.FC<VariantSelectorProps> = ({
  variants,
  selectedVariant,
  onSelect,
}) => {
  const localizedTexts = useLocalizedTexts();
  const headingId = useId();
  const groupName = useId();

  if (!variants.length) return null;

  return (
    <section className={styles.selector} data-component="variant-selector">
      <h3 className={styles.heading} id={headingId}>
        {t(localizedTexts, "Tracker.Products.ChooseVariant", "Choose version:")}
      </h3>
      <div
        className={styles.options}
        role="radiogroup"
        aria-labelledby={headingId}
      >
        {variants.map((variant) => (
          <label key={variant.id} className={styles.option}>
            <input
              type="radio"
              className={styles.radio}
              name={groupName}
              value={variant.id}
              checked={selectedVariant?.id === variant.id}
              onChange={() => onSelect(variant.id)}
            />
            <span className={styles.optionLabel}>
              {getVariantLabel(variant)}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
};

export default VariantSelector;
